import type { FC } from "react";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import { TextInput } from "@ui/components/Inputs/TextInput";
import { RegisterDeviceDialog } from "@ui/components/RegisterDeviceDialog";
import { useDeviceBreakpoint } from "@ui/hooks/useDeviceBreakpoint";
import { useUser } from "@ui/hooks/useUser";
import { useTranslations } from "@ui/providers/useTranslations";

import { LongArrowForwardIcon } from "@jti/brand-icons/long-arrow-forward";
import { Button } from "@jti/core-components/components/Button";
import type { DeviceAllowedCodeLength } from "@jti/fe-common";
import { getErrorMessageFromError } from "@jti/fe-common";
import { useDevices, useStartSurvey, useValidateSurveySerialCode } from "@jti/rest-api";
import type { SurveyType, UserSurvey } from "@jti/rest-api-types";

import { calculateDaysUntilNextWeek } from "../../utils";

import styles from "./SmartSurveyContent.module.scss";

export type SmartSurveyContentProps = {
  type: SurveyType;
  title: string;
  subtitle: string;
  description: string;
  preCtaText: string;
  ctaText?: string;
  ctaUrl?: string;
  inputPlaceholder: string;
  buttonText: string;
  isDisabled?: boolean;
  surveyMonkeyPageUrl: string;
  surveyInProgress?: UserSurvey;
  availableSurvey?: UserSurvey;
  completedRecurringSurvey?: UserSurvey;
  titleAllCompletedRecurringSurveys?: string;
  subtitleAllCompletedRecurringSurveys?: string;
  ctaButtonAllCompletedRecurringSurveys?: string;
  descriptionAllCompletedRecurringSurveys?: string;
  redirectUrlAllCompletedRecurringSurveys?: string;
  missingDeviceRegistrationDescription?: string;
  onRefetchAvailableSurveys?: () => void;
  // register device dialog props
  theme?: "light" | "dark";
  registerDeviceImgAlt?: string;
  registerDeviceImgMobile?: string;
  registerDeviceImgDesktop?: string;
  registerDeviceDialogTitle?: string;
  registerDeviceAccordionTitle?: string;
  registerDeviceDialogDescription?: string;
  registerDeviceAccordionButtonText?: string;
  registerDeviceAccordionButtonUrl?: string;
  registerDeviceAccordionDescription?: string;
  cancelDeviceRegistrationButtonText?: string;
  confirmDeviceRegistrationButtonText?: string;
  requiredCodeLength?: DeviceAllowedCodeLength[];
  isDeviceCodeFormatLabelVisible?: boolean;
};

type FormValues = {
  [SURVEY_CODE_FIELD_NAME]: string;
};

const SURVEY_CODE_FIELD_NAME = "surveyCode";

export const SmartSurveyContent: FC<SmartSurveyContentProps> = ({
  type,
  title,
  ctaText,
  ctaUrl,
  subtitle,
  preCtaText,
  buttonText,
  isDisabled,
  description,
  availableSurvey,
  inputPlaceholder,
  surveyInProgress,
  surveyMonkeyPageUrl,
  completedRecurringSurvey,
  onRefetchAvailableSurveys,
  titleAllCompletedRecurringSurveys,
  subtitleAllCompletedRecurringSurveys,
  ctaButtonAllCompletedRecurringSurveys,
  descriptionAllCompletedRecurringSurveys,
  redirectUrlAllCompletedRecurringSurveys,
  missingDeviceRegistrationDescription,

  // register device dialog props
  theme,
  requiredCodeLength,
  registerDeviceImgAlt,
  registerDeviceImgMobile,
  registerDeviceImgDesktop,
  registerDeviceDialogTitle,
  registerDeviceAccordionTitle,
  registerDeviceDialogDescription,
  registerDeviceAccordionButtonUrl,
  registerDeviceAccordionButtonText,
  cancelDeviceRegistrationButtonText,
  registerDeviceAccordionDescription,
  confirmDeviceRegistrationButtonText,
  isDeviceCodeFormatLabelVisible,
}) => {
  // TODO - remove once user?.has_registered_device is returned from the user API
  useDevices();
  const { breakpoint } = useDeviceBreakpoint();
  const { user } = useUser();
  const { __, dictionary } = useTranslations();
  const [isRegisterDevicePopupVisible, setIsRegisterDevicePopupVisible] = useState(false);

  const { isStartSurveyError, isStartSurveyLoading, startSurvey, startSurveyError, resetStartSurvey } =
    useStartSurvey();
  const {
    validateSurveySerialCode,
    resetValidateSurveySerialCode,
    validateSurveySerialCodeError,
    isValidateSurveySerialCodeError,
    isValidateSurveySerialCodeLoading,
  } = useValidateSurveySerialCode();

  const {
    control,
    setValue,
    formState: { errors },
    getValues,
    handleSubmit,
    clearErrors,
  } = useForm<FormValues>({
    defaultValues: {
      [SURVEY_CODE_FIELD_NAME]: "",
    },
  });

  const { start_day } = completedRecurringSurvey ?? {};
  const { is_only_for_device_owners } = surveyInProgress ?? availableSurvey ?? {};

  const isDeviceRegistrationRequired = is_only_for_device_owners && !user?.has_registered_device;
  const isMissingDeviceRegistrationDescriptionVisible =
    isDeviceRegistrationRequired && missingDeviceRegistrationDescription;

  const formErrorMessage = errors?.[SURVEY_CODE_FIELD_NAME]?.message;

  const isSurveyRequestError = isStartSurveyError || isValidateSurveySerialCodeError;

  const apiErrorMessage = getErrorMessageFromError(
    startSurveyError ?? validateSurveySerialCodeError ?? {},
    __("evo.fallback.error"),
    dictionary
  );

  const inputError = isSurveyRequestError ? apiErrorMessage : formErrorMessage;

  const titleToDisplay = useMemo(() => {
    if (isDisabled) {
      return titleAllCompletedRecurringSurveys;
    }
    return title;
  }, [isDisabled, title, titleAllCompletedRecurringSurveys]);

  const subtitleToDisplay = useMemo(() => {
    if (isDisabled) {
      const daysUntilNextSurvey = calculateDaysUntilNextWeek(start_day ?? undefined);
      return `${daysUntilNextSurvey} ${subtitleAllCompletedRecurringSurveys}`;
    }
    return subtitle;
  }, [isDisabled, start_day, subtitle, subtitleAllCompletedRecurringSurveys]);

  const descriptionToDisplay = useMemo(() => {
    if (isDisabled) {
      return descriptionAllCompletedRecurringSurveys;
    }
    return description;
  }, [isDisabled, description, descriptionAllCompletedRecurringSurveys]);

  const isLargeScreen = breakpoint === "desktop";

  const handleOpenDeviceRegistrationPopup = () => {
    setIsRegisterDevicePopupVisible(true);
  };

  const handleCloseDeviceRegistrationPopup = () => {
    onRefetchAvailableSurveys?.();
    setIsRegisterDevicePopupVisible(false);
  };

  const handleResetForm = () => {
    if (formErrorMessage) {
      clearErrors(SURVEY_CODE_FIELD_NAME);
    }
    if (isStartSurveyError) {
      resetStartSurvey();
    }
    if (isValidateSurveySerialCodeError) {
      resetValidateSurveySerialCode();
    }
  };

  const handleRedirectUser = () => {
    const { survey_id } = surveyInProgress ?? availableSurvey ?? {};
    const redirectUrl = `${surveyMonkeyPageUrl}?surveyType=${type}&uniqueCode=${survey_id}`;
    window.location.href = redirectUrl;
  };

  const handleValidateSurveySerialCode = async () => {
    const { surveyCode } = getValues();
    const { survey_id } = availableSurvey ?? {};

    await validateSurveySerialCode({
      userUuid: user?.uuid ?? "",
      ciamToken: user?.ciam_token ?? "",
      survey_id: survey_id ?? "",
      userInputCode: surveyCode,
    });
  };

  const handleStartSurvey = async () => {
    const { surveyCode } = getValues();
    const { survey_id } = availableSurvey ?? {};
    try {
      await startSurvey({
        userUuid: user?.uuid ?? "",
        ciamToken: user?.ciam_token ?? "",
        survey_id: survey_id ?? "",
        userInputCode: surveyCode,
      });
      handleRedirectUser();
      onRefetchAvailableSurveys?.();
    } catch (error) {
      console.error("Error starting survey:", error);
    }
  };

  const handleCheckDeviceRegistrationForSurveyInProgress = () => {
    const { is_only_for_device_owners } = surveyInProgress ?? {};

    if (is_only_for_device_owners && !user?.has_registered_device) {
      handleOpenDeviceRegistrationPopup();
      return;
    }
    handleRedirectUser();
  };

  const handleCheckDeviceRegistrationForNewSurvey = async () => {
    const { is_only_for_device_owners } = availableSurvey ?? {};

    if (is_only_for_device_owners && !user?.has_registered_device) {
      try {
        await handleValidateSurveySerialCode();
        handleOpenDeviceRegistrationPopup();
      } catch (error) {
        console.error("Error validating survey:", error);
      }
      return;
    }
    await handleStartSurvey();
  };

  const onSubmit = handleSubmit(async () => {
    if (surveyInProgress) {
      handleCheckDeviceRegistrationForSurveyInProgress();
      return;
    }

    await handleCheckDeviceRegistrationForNewSurvey();
  });

  const handleDeviceRegistrationSuccess = () => {
    handleCloseDeviceRegistrationPopup();
    if (surveyInProgress) {
      handleRedirectUser();
      return;
    }
    handleStartSurvey();
  };

  useEffect(() => {
    if (surveyInProgress) {
      const { serial_code } = surveyInProgress;
      setValue(SURVEY_CODE_FIELD_NAME, serial_code ?? "");
    }
  }, [setValue, surveyInProgress]);

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>{titleToDisplay}</h2>
      <h3 className={styles.subTitle}>{subtitleToDisplay}</h3>
      <p className={styles.description}>{descriptionToDisplay}</p>
      {isMissingDeviceRegistrationDescriptionVisible && (
        <p className={styles.description}>{missingDeviceRegistrationDescription}</p>
      )}
      {isDisabled ? (
        <Button href={redirectUrlAllCompletedRecurringSurveys} variant="text-muted" iconAfter={LongArrowForwardIcon}>
          {ctaButtonAllCompletedRecurringSurveys}
        </Button>
      ) : (
        <>
          <div className={styles.ctaWrapper}>
            <p className={styles.preCtaText}>{preCtaText}</p>
            <Button href={ctaUrl ?? ""} variant="text" size="sm" target="_blank">
              {ctaText}
            </Button>
          </div>
          <form onSubmit={onSubmit} className={styles.surveyCodeForm} noValidate>
            <Controller
              name={SURVEY_CODE_FIELD_NAME}
              control={control}
              rules={{
                required: { value: true, message: __("validation.field_required") },
              }}
              render={({ field, fieldState: { error } }) => (
                <TextInput
                  {...field}
                  errorText={inputError}
                  required
                  data-testid="surveyCodeInput"
                  placeholder={inputPlaceholder}
                  containerClassName={styles.input}
                  isError={!!error || isStartSurveyError || isValidateSurveySerialCodeError}
                  readOnly={
                    isDisabled || isStartSurveyLoading || !!surveyInProgress || isValidateSurveySerialCodeLoading
                  }
                  onFocus={handleResetForm}
                />
              )}
            />
            <Button
              type="submit"
              disabled={isDisabled}
              className={styles.submitButton}
              pending={isStartSurveyLoading || isValidateSurveySerialCodeLoading}
              size={isLargeScreen ? "md" : "sm"}
            >
              {buttonText}
            </Button>
          </form>
          {isRegisterDevicePopupVisible && (
            <RegisterDeviceDialog
              theme={theme}
              isOpened={true}
              imageAlt={registerDeviceImgAlt ?? ""}
              title={registerDeviceDialogTitle ?? ""}
              imgMobile={registerDeviceImgMobile ?? ""}
              imgDesktop={registerDeviceImgDesktop ?? ""}
              requiredCodeLength={requiredCodeLength ?? []}
              description={registerDeviceDialogDescription ?? ""}
              cancelButtonText={cancelDeviceRegistrationButtonText ?? ""}
              confirmButtonText={confirmDeviceRegistrationButtonText ?? ""}
              registerDeviceAccordionTitle={registerDeviceAccordionTitle ?? ""}
              registerDeviceAccordionButtonUrl={registerDeviceAccordionButtonUrl ?? ""}
              registerDeviceAccordionDescription={registerDeviceAccordionDescription ?? ""}
              registerDeviceAccordionButtonText={registerDeviceAccordionButtonText ?? ""}
              onClose={handleCloseDeviceRegistrationPopup}
              onDeviceRegistrationSuccess={handleDeviceRegistrationSuccess}
              isDeviceCodeFormatLabelVisible={isDeviceCodeFormatLabelVisible}
            />
          )}
        </>
      )}
    </div>
  );
};
